import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Form,
  Input,
  message,
  Space,
  Typography,
  Row,
  Col,
  Divider,
  Spin,
  Alert,
} from 'antd';
import {
  ContactsOutlined,
  SaveOutlined,
  ReloadOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  GlobalOutlined,
  UserOutlined,
  MedicineBoxOutlined,
} from '@ant-design/icons';
import { contactInfoService } from '../../services/contactInfoService';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ContactInfoManagement = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await contactInfoService.getContactInfo();
      setContactInfo(data);
      
      // Set form values
      form.setFieldsValue({
        clinicName: data.clinicName || '',
        doctorNames: data.doctorNames || '',
        specialties: data.specialties || '',
        street: data.address?.street || '',
        postalCode: data.address?.postalCode || '',
        city: data.address?.city || '',
        phone: data.phone || '',
        email: data.email || '',
        website: data.website || '',
      });
    } catch (error) {
      console.error(error);
      message.error('Fehler beim Laden der Kontaktinformationen');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const contactData = {
        clinicName: values.clinicName,
        doctorNames: values.doctorNames,
        specialties: values.specialties,
        address: {
          street: values.street,
          postalCode: values.postalCode,
          city: values.city,
        },
        phone: values.phone,
        email: values.email,
        website: values.website,
      };

      await contactInfoService.updateContactInfo(contactData);
      message.success('Kontaktinformationen erfolgreich aktualisiert');
      fetchData(); // Refresh data
    } catch (error) {
      console.error(error);
      message.error('Fehler beim Aktualisieren der Kontaktinformationen');
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickUpdate = async (field, value) => {
    try {
      await contactInfoService.updateContactField(field, value);
      message.success(`${field} erfolgreich aktualisiert`);
      fetchData();
    } catch (error) {
      console.error(error);
      message.error(`Fehler beim Aktualisieren von ${field}`);
    }
  };

  const renderCurrentInfo = () => {
    if (!contactInfo) return null;

    return (
      <Card 
        title={
          <Space>
            <ContactsOutlined />
            <span>Aktuelle Kontaktinformationen</span>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <div style={{ marginBottom: 16 }}>
              <Text strong>
                <HomeOutlined style={{ marginRight: 8 }} />
                Praxisname:
              </Text>
              <br />
              <Text>{contactInfo.clinicName || 'Nicht angegeben'}</Text>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <Text strong>
                <UserOutlined style={{ marginRight: 8 }} />
                Ärzte:
              </Text>
              <br />
              <Text>{contactInfo.doctorNames || 'Nicht angegeben'}</Text>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <Text strong>
                <MedicineBoxOutlined style={{ marginRight: 8 }} />
                Fachgebiete:
              </Text>
              <br />
              <Text>{contactInfo.specialties || 'Nicht angegeben'}</Text>
            </div>
          </Col>
          
          <Col span={12}>
            <div style={{ marginBottom: 16 }}>
              <Text strong>
                <HomeOutlined style={{ marginRight: 8 }} />
                Adresse:
              </Text>
              <br />
              <Text>
                {contactInfo.address ? (
                  <>
                    {contactInfo.address.street}<br />
                    {contactInfo.address.postalCode} {contactInfo.address.city}
                  </>
                ) : (
                  'Nicht angegeben'
                )}
              </Text>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <Text strong>
                <PhoneOutlined style={{ marginRight: 8 }} />
                Telefon:
              </Text>
              <br />
              <Text>{contactInfo.phone || 'Nicht angegeben'}</Text>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <Text strong>
                <MailOutlined style={{ marginRight: 8 }} />
                E-Mail:
              </Text>
              <br />
              <Text>{contactInfo.email || 'Nicht angegeben'}</Text>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <Text strong>
                <GlobalOutlined style={{ marginRight: 8 }} />
                Website:
              </Text>
              <br />
              <Text>{contactInfo.website || 'Nicht angegeben'}</Text>
            </div>
          </Col>
        </Row>
      </Card>
    );
  };

  return (
    <div style={{ padding: '24px', background: 'var(--app-bg, #f5f5f5)', minHeight: '100vh' }}>
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Title level={2} style={{ margin: 0 }}>
              <ContactsOutlined style={{ marginRight: 8 }} />
              Kontaktinformationen verwalten
            </Title>
            <Text type="secondary">Verwalten Sie die Kontaktdaten Ihrer Praxis</Text>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchData}
                loading={loading}
              >
                Aktualisieren
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Alert
        message="Kontaktinformationen"
        description="Diese Informationen werden auf Ihrer Website angezeigt. Stellen Sie sicher, dass alle Angaben aktuell und korrekt sind."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {renderCurrentInfo()}

      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Card 
            title="Kontaktinformationen bearbeiten"
            extra={
              <Button
                type="primary"
                icon={<SaveOutlined />}
                htmlType="submit"
                loading={submitting}
                size="large"
              >
                Alle Änderungen speichern
              </Button>
            }
          >
            <Row gutter={24}>
              <Col span={12}>
                <Title level={4}>
                  <HomeOutlined style={{ marginRight: 8 }} />
                  Praxisinformationen
                </Title>
                
                <Form.Item
                  name="clinicName"
                  label="Praxisname"
                  rules={[{ required: true, message: 'Bitte geben Sie den Praxisnamen ein' }]}
                >
                  <Input 
                    placeholder="z.B. Praxis am Ring"
                    suffix={
                      <Button 
                        type="link" 
                        size="small"
                        onClick={() => {
                          const value = form.getFieldValue('clinicName');
                          if (value) handleQuickUpdate('clinicName', value);
                        }}
                      >
                        Speichern
                      </Button>
                    }
                  />
                </Form.Item>

                <Form.Item
                  name="doctorNames"
                  label="Ärzte"
                  rules={[{ required: true, message: 'Bitte geben Sie die Ärzte ein' }]}
                >
                  <Input 
                    placeholder="z.B. Drs. Kuwert / Ziegenhorn und Aboumaila"
                    suffix={
                      <Button 
                        type="link" 
                        size="small"
                        onClick={() => {
                          const value = form.getFieldValue('doctorNames');
                          if (value) handleQuickUpdate('doctorNames', value);
                        }}
                      >
                        Speichern
                      </Button>
                    }
                  />
                </Form.Item>

                <Form.Item
                  name="specialties"
                  label="Fachgebiete"
                  rules={[{ required: true, message: 'Bitte geben Sie die Fachgebiete ein' }]}
                >
                  <TextArea 
                    rows={3}
                    placeholder="z.B. Ärzte für Allgemeinmedizin, Innere und Palliativmedizin"
                    showCount
                    maxLength={500}
                  />
                </Form.Item>

                <Divider />

                <Title level={4}>
                  <HomeOutlined style={{ marginRight: 8 }} />
                  Adresse
                </Title>

                <Form.Item
                  name="street"
                  label="Straße und Hausnummer"
                  rules={[{ required: true, message: 'Bitte geben Sie die Straße ein' }]}
                >
                  <Input 
                    placeholder="z.B. 164er Ring 9"
                    suffix={
                      <Button 
                        type="link" 
                        size="small"
                        onClick={() => {
                          const value = form.getFieldValue('street');
                          if (value) handleQuickUpdate('address.street', value);
                        }}
                      >
                        Speichern
                      </Button>
                    }
                  />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="postalCode"
                      label="PLZ"
                      rules={[{ required: true, message: 'Bitte geben Sie die PLZ ein' }]}
                    >
                      <Input 
                        placeholder="31785"
                        suffix={
                          <Button 
                            type="link" 
                            size="small"
                            onClick={() => {
                              const value = form.getFieldValue('postalCode');
                              if (value) handleQuickUpdate('address.postalCode', value);
                            }}
                          >
                            Speichern
                          </Button>
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={16}>
                    <Form.Item
                      name="city"
                      label="Stadt"
                      rules={[{ required: true, message: 'Bitte geben Sie die Stadt ein' }]}
                    >
                      <Input 
                        placeholder="Hameln"
                        suffix={
                          <Button 
                            type="link" 
                            size="small"
                            onClick={() => {
                              const value = form.getFieldValue('city');
                              if (value) handleQuickUpdate('address.city', value);
                            }}
                          >
                            Speichern
                          </Button>
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              <Col span={12}>
                <Title level={4}>
                  <PhoneOutlined style={{ marginRight: 8 }} />
                  Kontaktdaten
                </Title>

                <Form.Item
                  name="phone"
                  label="Telefonnummer"
                  rules={[
                    { required: true, message: 'Bitte geben Sie die Telefonnummer ein' },
                    { pattern: /^[\d\s\-+()\/]+$/, message: 'Ungültige Telefonnummer' }
                  ]}
                >
                  <Input 
                    placeholder="05151 24230"
                    prefix={<PhoneOutlined />}
                    suffix={
                      <Button 
                        type="link" 
                        size="small"
                        onClick={() => {
                          const value = form.getFieldValue('phone');
                          if (value) handleQuickUpdate('phone', value);
                        }}
                      >
                        Speichern
                      </Button>
                    }
                  />
                </Form.Item>

                <Form.Item
                  name="email"
                  label="E-Mail-Adresse"
                  rules={[
                    { required: true, message: 'Bitte geben Sie die E-Mail-Adresse ein' },
                    { type: 'email', message: 'Ungültige E-Mail-Adresse' }
                  ]}
                >
                  <Input 
                    placeholder="info@praxisamring-hameln.de"
                    prefix={<MailOutlined />}
                    suffix={
                      <Button 
                        type="link" 
                        size="small"
                        onClick={() => {
                          const value = form.getFieldValue('email');
                          if (value) handleQuickUpdate('email', value);
                        }}
                      >
                        Speichern
                      </Button>
                    }
                  />
                </Form.Item>

                <Form.Item
                  name="website"
                  label="Website (optional)"
                  rules={[
                    { type: 'url', message: 'Ungültige URL' }
                  ]}
                >
                  <Input 
                    placeholder="https://www.praxis-beispiel.de"
                    prefix={<GlobalOutlined />}
                    suffix={
                      <Button 
                        type="link" 
                        size="small"
                        onClick={() => {
                          const value = form.getFieldValue('website');
                          if (value) handleQuickUpdate('website', value);
                        }}
                      >
                        Speichern
                      </Button>
                    }
                  />
                </Form.Item>

                <Alert
                  message="Einzelfeld-Updates"
                  description="Sie können einzelne Felder sofort speichern, indem Sie auf 'Speichern' neben dem jeweiligen Feld klicken, oder alle Änderungen auf einmal mit dem Hauptspeicher-Button."
                  type="warning"
                  showIcon
                  style={{ marginTop: 24 }}
                />
              </Col>
            </Row>
          </Card>
        </Form>
      </Spin>
    </div>
  );
};

export default ContactInfoManagement;
